/**
 * components/print/SuratJalanCabang.js
 * Surat Jalan Cabang (PRD §5.5) — describes PACKAGES, not contents, exactly like Surat Jalan
 * Pusat: no item list at all, just `packing.suratJalanRows` (quantity + free-text description
 * per row), captured by the SAME `SuratJalanDialog.js` (PRD §5.5: "the two surat jalan carry
 * identical data — only the column headers differ... so one row editor serves both") — reused
 * here UNMODIFIED, as T2c-2 anticipated when it named that dialog generically.
 *
 * Portal-based, same pattern as every other document — `createPortal(..., document.body)`,
 * renders on `.print-doc--cabang`.
 *
 * **No `items`/`company` prop** — no item list, no letterhead, matching Surat Jalan Pusat's own
 * shape exactly.
 *
 * **`SURAT JALAN No. {txn.invoiceNumber}` is the SAME number as this transaction's Invoice
 * Cabang** — Phase T2a removed the old `suratJalanCabangNo` field specifically because a cabang
 * surat jalan shares the invoice's own minted nota number; there is only one `invoiceNumber`
 * field on a transaction, period, and this document simply reads it, same as `InvoiceCabang.js`
 * does. No second number is ever minted for this document.
 *
 * Footer reuses the EXACT `.print-doc__footer print-doc__footer--plain` markup already built
 * for Surat Jalan Pusat (T2c-2/T2c-2-fix-2) — not rebuilt here.
 *
 * **cabang-fix session (round 1)**: the `Tuan / Toko :` buyer block moved from a standalone
 * full-width block below the header into the RIGHT header column (stacked under the date), and
 * the handwritten `dengan kendaraan ________________ No. ____________` vehicle line was deleted
 * entirely — neither is present in the real Cahaya Gunung Sejati reference photo this document
 * was actually built against. This measurably shrank header height.
 *
 * **cabang-fix session, round 2**: a real client report ("large gap between header and table")
 * turned out, once actually measured (`getBoundingClientRect` under `page.emulateMediaType
 * ('print')`), to NOT be a header-to-table gap at all — that gap measured exactly 10px (just the
 * header's own inline `marginBottom`, nothing more). The real slack was 53.5px of UNUSED space
 * below the footer, left over from round 1's header shrink never being reflected in the row
 * budget below — see that constant's own updated comment. (Also checked and ruled out as a
 * contributor: `.print-doc__header`'s row-direction flex stretches its 1-line left column to
 * match the 4-line right column's height — real, confirmed via the same measurement pass — but
 * this doesn't move either column's visible text position or the header's own outer height,
 * since a flex row's height is governed by its tallest item regardless of `align-items`; it has
 * zero effect on the reported gap and was left untouched.)
 *
 * **Address overrides for printing**: a screen-only editor sits to the LEFT of the document,
 * side by side (`.print-doc-overlay__body` — see InvoicePusat.js's own header comment for the
 * full layout story, incl. the narrow-viewport stacked fallback). Lets staff correct the buyer
 * address for THIS print only, defaulting from the
 * FROZEN `txn.buyerBlock.address` (never a live Contact lookup — see InvoicePusat.js's own
 * header comment for the full reasoning) and persisting to
 * `txn.addressOverrides.suratJalanCabang` only on Cetak. A blank field blocks Cetak.
 *
 * **The cabang @page geometry fix**: `.print-doc--cabang` (below) now sits inside a page
 * declared at the SAME 8.5in×11in geometry as `.print-doc--pusat` — a real physical print of
 * Surat Jalan Pusat (this document's own sibling) overflowed onto a second sheet on the real
 * EPSON LX-300+II, traced to the driver picking the wrong paper preset for a landscape-shaped
 * `8.5in×5.5in` page request; this document shares the exact same `@page cabang` rule and is
 * fixed by the same change. This document's OWN layout/rows/
 * `ROWS_PER_PAGE_SURAT_JALAN_CABANG` are UNCHANGED — see `printFoundation.css`'s own header
 * comment (last dated paragraph) for the full investigation.
 */
import React, { useState } from "react";
import { createPortal } from "react-dom";
import "./printFoundation.css";
import { paginateLines, NBSP, PRINT_SETTINGS_REMINDER_CABANG } from "../../utils/pagination";
import { fmtDateLong } from "../../utils/idGenerators";
import AddressOverrideField from "./AddressOverrideField";

// Measured pre-cabang-fix (round 1), against the OLD header shape (standalone buyer block +
// vehicle line below it): 202.4px available for data rows at a real measured row height of
// 26.98px → floor(202.4/26.98) = 7. **Re-measured for real in round 2** against the ACTUAL
// post-round-1 header (84px, confirmed via getBoundingClientRect under print-media emulation —
// see the file header comment): page content height 422.4px − 84px header − 10px header-to-table
// gap − 26.98px thead − 20px fixed footer gap − 38px footer = 243.42px available for data rows,
// ÷ 26.98px per row = floor(9.02) → naively 9, but the REAL measured leftover at 7 rows was only
// 53.53px (small arithmetic differences between the formula and a direct real-DOM measurement,
// same caution `ROWS_PER_PAGE_CABANG`'s own history already flags — trust the real number, not
// the formula): 53.53 / 26.98 = 1.98 additional rows, so `8` is the confirmed real ceiling (26.5px
// residual, comfortably safe), NOT `9` (which computes to −0.44px, a real overflow). Confirmed via
// `page.pdf()`: 8 rows fill one real physical page with the footer visible and not clipped; 9
// genuinely split onto a second page. `8` also now matches Surat Jalan Pusat's own
// `ROWS_PER_PAGE_SURAT_JALAN` — round 1's header shrink brought this document's own header close
// enough to its sibling's that the two row budgets converge.
//
// Print-width foundational correction — RE-VERIFIED, CONFIRMED UNCHANGED at 8. The Nama Barang
// column shrank from 6.9in to 6.4in along with every other document once .print-doc--cabang's
// content width was corrected 8.7in to 8.2in (see printFoundation.css's own header comment).
// Unlike ROWS_PER_PAGE_SURAT_JALAN (Surat Jalan Pusat's own sibling constant, which needed
// lowering to 7 under the identical width cut), this document's own real page.pdf()
// re-measurement -- 8 rows, the SAME worst-case long (~85-character) description that broke
// Surat Jalan Pusat at 8 -- still lands at exactly 480px, zero overflow. The difference: this
// document's own 26.5px real safety margin (round 2's own measurement, above) was enough to
// absorb the new wrap cost where Surat Jalan Pusat's smaller margin wasn't -- a real example of
// two visually similar documents needing genuinely different treatment, not an assumption
// carried over from one to the other. Re-verify via page.pdf() before assuming this unchanged
// if either the header or the column width changes again.
const ROWS_PER_PAGE_SURAT_JALAN_CABANG = 8;

export default function SuratJalanCabang({ txn, onClose, onSaveAddressOverride }) {
  const buyer = txn.buyerBlock || { name: "", address: "" };
  const savedOverride = txn.addressOverrides?.suratJalanCabang;
  const [buyerAddressOverride, setBuyerAddressOverride] = useState(
    () => savedOverride?.buyerAddress ?? (txn.buyerBlock?.address || "")
  );
  const canPrint = buyerAddressOverride.trim() !== "";
  const handleCetak = () => {
    if (!canPrint) return;
    onSaveAddressOverride?.(txn.id, "suratJalanCabang", { buyerAddress: buyerAddressOverride.trim() });
    window.print();
  };
  const rows = txn.packing?.suratJalanRows || [];
  // Same dummy-value adaptation as SuratJalanPusat.js — this document has no money concept
  // either, so qty:1/unitPrice:0 satisfy paginateLines' internal txnTotal call without meaning
  // anything; only page.lines/pageCount are read below.
  const { pages } = paginateLines(
    rows.map((r) => ({ ...r, qty: 1, unitPrice: 0 })),
    ROWS_PER_PAGE_SURAT_JALAN_CABANG
  );

  return createPortal(
    <div className="print-doc-overlay">
      <div className="print-doc-overlay__actions">
        <span className="print-doc-overlay__note">Surat Jalan Cabang — {txn.invoiceNumber || txn.id}</span>
        <span className="print-doc-overlay__reminder">{PRINT_SETTINGS_REMINDER_CABANG}</span>
        <button type="button" className="btn" onClick={onClose}>Tutup</button>
        <button type="button" className="btn btn-primary" onClick={handleCetak} disabled={!canPrint}>Cetak</button>
      </div>

      <div className="print-doc-overlay__body">
        <div className="print-doc-overlay__override-panel">
          <AddressOverrideField label="Alamat Pembeli" value={buyerAddressOverride} onChange={setBuyerAddressOverride} />
        </div>

        <div className="print-doc print-doc--cabang">
        {pages.map((page) => {
          const fillerCount = Math.max(0, ROWS_PER_PAGE_SURAT_JALAN_CABANG - page.lines.length);
          return (
            <div className="print-page" key={page.pageNumber}>
              <div className="print-doc__header" style={{ marginBottom: 10 }}>
                {/* cabang-fix round 3: shifted down by exactly one real line-height (21px,
                    measured via getBoundingClientRect under print-media emulation — matches the
                    right column's own per-line height exactly) so "Surat Jalan No." lands level
                    with "Tuan / Toko :" (the right column's SECOND line), not the date line
                    above it — the pairing the client asked for: the two bold label lines
                    aligned with each other. */}
                <div className="print-doc__header-col" style={{ marginTop: 21 }}>
                  <span className="print-doc__label">Surat Jalan No. </span>{txn.invoiceNumber}
                </div>
                <div className="print-doc__header-col print-doc__header-col--right">
                  <div>Jakarta, {fmtDateLong(txn.date)}</div>
                  <div className="print-doc__label">Tuan / Toko :</div>
                  <div>{buyer.name}</div>
                  {buyerAddressOverride.split("\n").map((line, i) => (
                    <div key={i}>{line}</div>
                  ))}
                </div>
              </div>

              <div className="print-doc__table-area">
                <table className="print-doc__table">
                  <colgroup>
                    <col style={{ width: "1in" }} />
                    <col />
                  </colgroup>
                  <thead>
                    <tr>
                      <th>Banyaknya</th>
                      <th>Nama Barang</th>
                    </tr>
                  </thead>
                  <tbody>
                    {page.lines.map((r, i) => (
                      <tr key={i}>
                        <td>{r.quantity}</td>
                        <td className="wrap">{r.description}</td>
                      </tr>
                    ))}
                    {Array.from({ length: fillerCount }).map((_, i) => (
                      <tr className="print-doc__filler" key={`filler-${i}`}>
                        <td>{NBSP}</td><td>{NBSP}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="print-doc__footer print-doc__footer--plain">
                <div className="print-doc__footer-cell">
                  <div className="print-doc__footer-label">Tanda Terima,</div>
                  <div className="print-doc__footer-line" />
                </div>
                <div className="print-doc__footer-cell print-doc__footer-cell--center" />
                <div className="print-doc__footer-cell" style={{ alignItems: "flex-end", textAlign: "right" }}>
                  <div className="print-doc__footer-label">Hormat Kami,</div>
                  <div className="print-doc__footer-line" />
                </div>
              </div>
            </div>
          );
        })}
        </div>
      </div>
    </div>,
    document.body
  );
}
